/**
 * Maps Shopware Store API errors from the checkout to customer-facing i18n keys and to the
 * PII-free codes reported to Sentry.
 *
 * Deliberately free of Nuxt and Sentry imports so it stays a pure function of the error.
 */

export interface ShopwareApiErrorEntry {
    status?: string
    code?: string
    detail?: string
    meta?: { parameters?: Record<string, unknown> }
}

const CART_INVALID = 'CHECKOUT__CART_INVALID'

/**
 * `error-code` for client-side `handlePayment` failures. Those are reported to Sentry with their full
 * codes where they happen, so `/checkout/error` must not report them a second time.
 */
export const PAYMENT_HANDLE_FAILED = 'CHECKOUT__PAYMENT_HANDLE_FAILED'

export const PAYMENT_CUSTOMER_CANCELED = 'CHECKOUT__CUSTOMER_CANCELED_EXTERNAL_PAYMENT'

/** Cart error keys (without the appended product id) grouped by customer-facing message. */
const CART_ERROR_GROUPS: ReadonlyArray<{ key: string, prefixes: readonly string[] }> = [
    {
        key: 'checkout.error.order.productUnavailable',
        prefixes: ['product-out-of-stock', 'product-not-found', 'product-stock-reached', 'min-order-quantity', 'purchase-steps-quantity']
    },
    { key: 'checkout.error.order.paymentMethodBlocked', prefixes: ['payment-method-blocked'] },
    { key: 'checkout.error.order.shippingMethodBlocked', prefixes: ['shipping-method-blocked'] },
    {
        key: 'checkout.error.order.addressInvalid',
        prefixes: ['shipping-address-blocked', 'billing-address-blocked', 'address-invalid', 'salutation-missing-', 'country-region-missing-']
    }
]

const CART_ERROR_PREFIXES: readonly string[] = CART_ERROR_GROUPS.flatMap(group => group.prefixes)

function isRecord (value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

/** Shopware `errors[]` from a hubble `ApiError` (`e.body`), or `[]` for anything else. */
export function getShopwareErrors (e: unknown): ShopwareApiErrorEntry[] {
    if (!isRecord(e) || !Array.isArray(e.body)) {
        return []
    }

    return e.body.filter(isRecord) as ShopwareApiErrorEntry[]
}

export function getHttpStatus (e: unknown): number | undefined {
    return isRecord(e) && typeof e.statusCode === 'number' ? e.statusCode : undefined
}

/**
 * Cart error keys from a `CHECKOUT__CART_INVALID` entry. Shopware serialises them into one string,
 * one `"<key><productId>: <message>"` per line; only the known key prefix is kept, so product
 * names and messages never leave this function.
 */
function getCartErrorKeys (entry: ShopwareApiErrorEntry): string[] {
    const raw = entry.meta?.parameters?.errors

    if (typeof raw !== 'string') {
        return []
    }

    return raw
        .split('\n')
        .map(line => line.split(':')[0]?.trim() ?? '')
        .map(id => CART_ERROR_PREFIXES.find(prefix => id.startsWith(prefix)) ?? 'unknown-cart-error')
}

/** Top-level Shopware codes plus the cart error keys nested in `CHECKOUT__CART_INVALID`. */
export function getShopwareErrorCodes (e: unknown): string[] {
    return getShopwareErrors(e).flatMap((entry) => {
        const code = entry.code ?? 'UNKNOWN'

        return code === CART_INVALID ? [code, ...getCartErrorKeys(entry)] : [code]
    })
}

/** i18n key for a failed `placeOrder`. */
export function resolveOrderErrorKey (e: unknown): string {
    const codes = getShopwareErrorCodes(e)

    if (codes.includes('CHECKOUT__CUSTOMER_NOT_LOGGED_IN')) {
        return 'checkout.validation.loginRequired'
    }

    if (codes.includes('CHECKOUT__CART_EMPTY')) {
        return 'checkout.error.order.cartEmpty'
    }

    const group = CART_ERROR_GROUPS.find(({ prefixes }) => prefixes.some(prefix => codes.includes(prefix)))

    return group?.key ?? 'checkout.error.order.generic'
}

/** i18n key for the `error-code` query on `/checkout/error`, or `''` when there is nothing to show. */
export function resolvePaymentErrorKey (code: string | undefined): string {
    if (code == null || code === '') {
        return ''
    }

    return code === PAYMENT_CUSTOMER_CANCELED
        ? 'checkout.error.payment.canceled'
        : 'checkout.error.payment.failed'
}
